// Ready-made Facebook Ad templates for शिक्षा मित्र.
// Selecting one pre-fills the "New Ad" form (name/headline/body/CTA) — fully editable,
// previewable, and one-click Create. Add your school photo before posting.
//
// Each template caption is composed as:  unique emoji HOOK → caption → strong CTA → hashtags.
// Hashtags = brand tags + template-specific tags (relevant to that exact feature) for
// better reach/discoverability on Facebook & Instagram. (Hashtags appear only in the ad
// caption — NOT in the Click-to-WhatsApp pre-filled message.)

export const AD_TEMPLATE_CATEGORIES = [
  { key: 'modules', label: '🏫 Core Modules (8)' },
  { key: 'ai_admin', label: '🤖 AI — Admin (8)' },
  { key: 'ai_teacher', label: '🤖 AI — Teacher (6)' },
  { key: 'ai_student', label: '🤖 AI — Student (11)' },
  { key: 'ai_parent', label: '🤖 AI — Parent (6)' },
  { key: 'admission', label: '🎯 Admission Engine' },
  { key: 'trust', label: '🛡️ Trust & BYODB' },
];

// Brand hashtags added to every template (template-specific tags are passed per template).
const BRAND_TAGS = '#शिक्षामित्र #ShikshaMitra';

// Strong closing CTA line — varies by the ad's call-to-action.
const ctaLineFor = (cta) =>
  cta === 'SEND_WHATSAPP_MESSAGE' ? '👉 अभी WhatsApp पर Free Demo book करें!'
  : cta === 'APPLY_NOW' ? '👉 अभी Apply करें — सीटें सीमित! 🎯'
  : '👉 आज ही Free Demo बुक करें — 100% FREE! 🙏';

// T(id, cat, title, name, headline, hook, body, tags, cta)
//   hook  = unique emoji-rich opening line for this template
//   body  = the caption (benefit-led)
//   tags  = template-specific hashtags (brand tags auto-prepended)
const T = (id, cat, title, name, headline, hook, body, tags, cta = 'LEARN_MORE') => ({
  id, cat, title, name, headline,
  body: `${hook}\n\n${body}\n\n${ctaLineFor(cta)}\n${BRAND_TAGS} ${tags}`,
  hashtags: `${BRAND_TAGS} ${tags}`,
  hook,
  cta,
});

export const AD_TEMPLATES = [
  // ---------------- 8 CORE MODULES ----------------
  T('mod_students', 'modules', 'छात्र प्रबंधन', 'Module - Students',
    'हर बच्चे की पूरी जानकारी, एक जगह',
    '📋 हर बच्चे की पूरी कुंडली — एक click में!',
    'Profile, documents, Excel से bulk import — रजिस्टर का झंझट खत्म! 100% हिंदी में, 100% FREE platform।',
    '#StudentManagement #छात्रप्रबंधन #SchoolERP #DigitalSchool'),
  T('mod_teachers', 'modules', 'शिक्षक प्रबंधन', 'Module - Teachers',
    'पूरा staff record एक जगह',
    '👨‍🏫 पूरा staff record — अब उँगलियों पर!',
    'Teacher profile, subject mapping, leave व attendance — सब digital। FREE platform, हिंदी में।',
    '#StaffManagement #TeacherRecords #SchoolERP #DigitalSchool'),
  T('mod_attendance', 'modules', 'Attendance', 'Module - Attendance',
    'एक click में Attendance',
    '✅ रजिस्टर को कहिए अलविदा!',
    'Digital attendance + absent होने पर parents को auto WhatsApp alert। 100% FREE।',
    '#DigitalAttendance #उपस्थिति #SchoolApp #AttendanceAlert'),
  T('mod_exams', 'modules', 'परीक्षा व परिणाम', 'Module - Exams',
    'Exam से Report Card तक — आसान',
    '📝 Exam से Report Card तक — झटपट!',
    'Marks, grades, rank list और AI से report card — seconds में। हिंदी में, FREE platform।',
    '#ExamManagement #ReportCard #परीक्षा #ResultManagement'),
  T('mod_fees', 'modules', 'फीस प्रबंधन', 'Module - Fees',
    'फीस वसूली का तनाव खत्म',
    '💰 फीस वसूली का तनाव — अब खत्म!',
    'Online collection, digital receipt, pending list और WhatsApp पर auto fee reminder। 100% FREE।',
    '#FeeManagement #OnlineFees #फीस #SchoolFees'),
  T('mod_timetable', 'modules', 'Timetable व Calendar', 'Module - Timetable',
    'पूरा साल पहले से planned',
    '📅 पूरा साल पहले से planned!',
    'Class timetable, teacher schedule, academic calendar व holidays — सब एक जगह। FREE platform।',
    '#Timetable #SchoolCalendar #समयसारिणी #SchoolPlanning'),
  T('mod_communication', 'modules', 'Communication', 'Module - Communication',
    'Parents तक तुरंत — phone calls 70% कम',
    '📢 Parents तक तुरंत — phone calls 70% कम!',
    'Announcements, circulars और WhatsApp/SMS/Email — सीधे platform से। हिंदी में, FREE।',
    '#SchoolCommunication #ParentConnect #WhatsAppForSchools', 'SEND_WHATSAPP_MESSAGE'),
  T('mod_transport', 'modules', 'Transport प्रबंधन', 'Module - Transport',
    'Bus, route व बच्चों की सुरक्षा',
    '🚌 बच्चों की सुरक्षा — हर route पर!',
    'Bus routes, driver details, transport fee व emergency contacts — सब track। 100% FREE platform।',
    '#SchoolTransport #BusTracking #StudentSafety #परिवहन'),

  // ---------------- AI: ADMIN (8) ----------------
  T('ai_notice', 'ai_admin', 'Notice Simplifier', 'AI - Notice Simplifier',
    'कठिन Notice — आसान हिंदी में',
    '📄 कठिन Notice — पल में आसान हिंदी में!',
    'AI किसी भी notice को सरल, समझने लायक हिंदी में बदल दे। शिक्षा मित्र की 31 AI features में से एक — 100% FREE।',
    '#NoticeSimplifier #AIforSchools #हिंदी #EdTech'),
  T('ai_fee_reminder', 'ai_admin', 'AI Fee Reminder', 'AI - Fee Reminder',
    'AI से असरदार Fee Reminder',
    '💬 AI लिखे, आप भेजें — फीस आए समय पर!',
    'सही शब्दों में, polite reminder messages — AI तैयार करे, आप भेजें। फीस वसूली आसान। FREE platform।',
    '#FeeReminder #AIforSchools #फीस #SchoolFees'),
  T('ai_comm_templates', 'ai_admin', 'Communication Templates', 'AI - Communication Templates',
    'हर मौके के लिए तैयार संदेश',
    '✍️ हर मौके का संदेश — पहले से तैयार!',
    'त्योहार, छुट्टी, meeting — हर occasion के लिए AI से ready message। समय बचाएँ। 31 AI features, FREE।',
    '#MessageTemplates #AIforSchools #SchoolCommunication #EdTech'),
  T('ai_workflow', 'ai_admin', 'Workflow Reminders', 'AI - Workflow Reminders',
    'ज़रूरी काम कभी न भूलें',
    '⏰ ज़रूरी काम अब कभी न छूटे!',
    'AI आपके रोज़ के स्कूल कामों के reminders दे — कुछ छूटे नहीं। Principal का दफ्तर अब seconds में। FREE।',
    '#WorkflowReminders #PrincipalTools #AIforSchools #SchoolAdmin'),
  T('ai_compliance', 'ai_admin', 'Compliance Checklist', 'AI - Compliance Checklist',
    'Government compliance — tension free',
    '📋 Government compliance — अब tension free!',
    'AI से ready compliance checklist — UDISE+ व ज़रूरी काम समय पर। हफ्तों का काम मिनटों में। 100% FREE।',
    '#Compliance #UDISE #AIforSchools #PrincipalTools'),
  T('ai_report_card', 'ai_admin', 'Report Card Generator', 'AI - Report Card Generator',
    'AI से Report Card — seconds में',
    '📊 Marks डालें — AI Report Card बना दे!',
    'Marks डालें, AI सुंदर report card बना दे — remarks सहित। समय व मेहनत दोनों बचें। FREE platform।',
    '#ReportCardGenerator #AIforSchools #रिपोर्टकार्ड #EdTech'),
  T('ai_complaint_reply', 'ai_admin', 'Complaint Reply', 'AI - Complaint Reply',
    'शिकायत का smart, शांत जवाब',
    '🤝 हर शिकायत का शांत, smart जवाब!',
    'AI parents की शिकायत का professional जवाब सुझाए — रिश्ते बेहतर। 31 AI features में से एक। FREE।',
    '#ComplaintManagement #AIforSchools #ParentRelations #EdTech'),
  T('ai_complaint_guide', 'ai_admin', 'Complaint Guidance', 'AI - Complaint Guidance',
    'किस शिकायत पर क्या करें',
    '🧭 किस शिकायत पर क्या करें — AI बताए!',
    'AI आपको सही action बताए — हर शिकायत का सही हल। स्कूल की reputation मज़बूत। 100% FREE।',
    '#ComplaintGuidance #PrincipalTools #AIforSchools #SchoolAdmin'),

  // ---------------- AI: TEACHER (6) ----------------
  T('ai_content_improver', 'ai_teacher', 'Content Improver', 'AI - Content Improver',
    'पढ़ाई की सामग्री और बेहतर',
    '✨ पढ़ाई की सामग्री — और भी असरदार!',
    'AI आपके teaching content को आसान व रोचक बनाए — बच्चे बेहतर समझें। शिक्षकों के लिए AI। FREE platform।',
    '#TeachingContent #AITeaching #TeacherTools #EdTech'),
  T('ai_progress_summary', 'ai_teacher', 'Progress Summary', 'AI - Progress Summary',
    'हर बच्चे की प्रगति — एक सार में',
    '📈 हर बच्चे की प्रगति — एक सार में!',
    'AI class व student progress का छोटा, साफ़ summary बनाए। समय बचे, ध्यान बढ़े। 31 AI features, FREE।',
    '#StudentProgress #AITeaching #TeacherTools #EdTech'),
  T('ai_student_support', 'ai_teacher', 'Student Support', 'AI - Student Support',
    'कमज़ोर बच्चों की सही मदद',
    '🤲 कमज़ोर बच्चों की सही मदद — समय पर!',
    'AI बताए किस बच्चे को कैसे support चाहिए — कोई पीछे न छूटे। शिक्षकों का AI साथी। 100% FREE।',
    '#StudentSupport #AITeaching #TeacherTools #StudentSuccess'),
  T('ai_weak_topics', 'ai_teacher', 'Class Weak Topics', 'AI - Class Weak Topics',
    'कौन-से topics कमज़ोर — AI बताए',
    '🎯 कौन-से topics कमज़ोर — AI पकड़े!',
    'पूरी class के weak topics पहचानें और focus करें। बेहतर results, कम मेहनत। FREE platform।',
    '#WeakTopics #AITeaching #TeacherTools #ExamPrep'),
  T('ai_at_risk', 'ai_teacher', 'At-Risk Alerts', 'AI - At-Risk Alerts',
    'जो बच्चे struggle कर रहे — पहले जानें',
    '🚨 जो बच्चे पिछड़ रहे — पहले जानें!',
    'AI समय रहते alert करे ताकि सही मदद मिल सके। हर बच्चे की सफलता। 31 AI features, FREE।',
    '#AtRiskAlerts #AITeaching #StudentSuccess #TeacherTools'),
  T('ai_question_paper', 'ai_teacher', 'Question Paper Generator', 'AI - Question Paper Generator',
    'AI से Question Paper — मिनटों में',
    '📝 Question Paper — मिनटों में तैयार!',
    'Class, subject व difficulty चुनें — AI पूरा question paper बना दे। शिक्षकों का समय बचाएँ। 100% FREE।',
    '#QuestionPaper #AITeaching #TeacherTools #ExamPrep'),

  // ---------------- AI: STUDENT (11) ----------------
  T('ai_study_plan', 'ai_student', 'Study Plan Generator', 'AI - Study Plan Generator',
    'हर बच्चे का अपना Study Plan',
    '🗓️ हर बच्चे का अपना Study Plan!',
    'AI बच्चे के लिए personalized पढ़ाई plan बनाए — रोज़ क्या पढ़ें, साफ़। बच्चों का AI साथी। FREE।',
    '#StudyPlan #AITutor #पढ़ाई #StudentLearning'),
  T('ai_micro_tasks', 'ai_student', 'Micro Tasks', 'AI - Micro Tasks',
    'बड़ी पढ़ाई — छोटे आसान tasks में',
    '🧩 बड़ी पढ़ाई — छोटे आसान tasks में!',
    'AI मुश्किल chapters को छोटे steps में बाँटे — बच्चे आसानी से पूरा करें। 31 AI features, FREE platform।',
    '#MicroLearning #AITutor #पढ़ाई #StudentLearning'),
  T('ai_mcq', 'ai_student', 'MCQ Generator', 'AI - MCQ Generator',
    'किसी भी chapter के MCQs — तुरंत',
    '❓ किसी भी chapter के MCQs — तुरंत!',
    'AI अभ्यास के लिए MCQs बना दे — practice से तैयारी मज़बूत। बच्चों के लिए AI। 100% FREE।',
    '#MCQ #AITutor #ExamPrep #StudentLearning'),
  T('ai_homework_hints', 'ai_student', 'Homework Hints', 'AI - Homework Hints',
    'Homework में अटके? AI से Hint लें',
    '💡 Homework में अटके? AI से Hint लें!',
    'सीधे answer नहीं — समझ बढ़ाने वाले hints। बच्चे खुद सीखें। शिक्षा मित्र AI, FREE platform।',
    '#HomeworkHelp #AITutor #पढ़ाई #StudentLearning'),
  T('ai_practice_tests', 'ai_student', 'Practice Tests', 'AI - Practice Tests',
    'AI से Practice Test — exam की तैयारी',
    '🧪 Practice Test से exam की पक्की तैयारी!',
    'अपनी class व subject के practice tests — कमज़ोरी पहचानें, सुधारें। बच्चों का AI। FREE।',
    '#PracticeTest #AITutor #ExamPrep #StudentLearning'),
  T('ai_weekly_challenge', 'ai_student', 'Weekly Challenge', 'AI - Weekly Challenge',
    'हर हफ्ते नया Learning Challenge',
    '🏆 हर हफ्ते नया Learning Challenge!',
    'AI से fun weekly challenges — पढ़ाई में मज़ा व नियमितता। 31 AI features में से एक। 100% FREE।',
    '#LearningChallenge #AITutor #FunLearning #StudentLearning'),
  T('ai_gamified', 'ai_student', 'Gamified Learning', 'AI - Gamified Learning',
    'पढ़ाई अब game जैसी मज़ेदार',
    '🎮 पढ़ाई अब game जैसी मज़ेदार!',
    'Points, badges व leaderboard — बच्चे खेल-खेल में सीखें। पढ़ाई में रुचि बढ़े। FREE platform।',
    '#GamifiedLearning #AITutor #FunLearning #पढ़ाई'),
  T('ai_revision', 'ai_student', 'Revision Scheduler', 'AI - Revision Scheduler',
    'समय पर Revision — AI याद दिलाए',
    '🔁 समय पर Revision — AI याद दिलाए!',
    'AI smart revision schedule बनाए ताकि पढ़ा हुआ याद रहे। बेहतर तैयारी, कम तनाव। 100% FREE।',
    '#RevisionScheduler #AITutor #ExamPrep #StudentLearning'),
  T('ai_chapter_explain', 'ai_student', 'Chapter Explanation', 'AI - Chapter Explanation',
    'कोई भी Chapter — आसान भाषा में',
    '📖 कोई भी Chapter — आसान भाषा में!',
    'AI मुश्किल chapter को सरल हिंदी में समझाए — बच्चे जल्दी समझें। बच्चों का AI साथी। FREE।',
    '#ChapterExplained #AITutor #पढ़ाई #StudentLearning'),
  T('ai_hw_assistant', 'ai_student', 'Homework Assistant', 'AI - Homework Assistant',
    'Homework में AI की मदद',
    '✏️ Homework में step-by-step AI मदद!',
    'AI step-by-step समझाकर homework पूरा करने में मदद करे — खुद सीखते हुए। 31 AI features, FREE।',
    '#HomeworkAssistant #AITutor #पढ़ाई #StudentLearning'),
  T('ai_doubt_history', 'ai_student', 'Doubt History', 'AI - Doubt History',
    'हर पूछा सवाल — संभाल कर',
    '📚 हर पूछा सवाल — संभाल कर रखा!',
    'बच्चे के सभी doubts व जवाब एक जगह — revision के लिए बढ़िया। शिक्षा मित्र AI। 100% FREE।',
    '#DoubtHistory #AITutor #पढ़ाई #StudentLearning'),

  // ---------------- AI: PARENT (6) ----------------
  T('ai_portal_simplifier', 'ai_parent', 'Portal Simplifier', 'AI - Portal Simplifier',
    'Parent Portal — आसान हिंदी में',
    '📱 Parent Portal — आसान हिंदी में!',
    'AI parents को portal सरल भाषा में समझाए — हर जानकारी आसानी से। माता-पिता हमेशा जुड़े। FREE।',
    '#ParentPortal #ParentConnect #अभिभावक #EdTech'),
  T('ai_life_skills', 'ai_parent', 'Life Skills Tips', 'AI - Life Skills Tips',
    'बच्चों के लिए Parenting Tips',
    '🌱 रोज़ नए Parenting Tips — बेहतर परवरिश!',
    'AI से रोज़ उपयोगी parenting व life-skills सुझाव — बेहतर परवरिश। 31 AI features में से एक। 100% FREE।',
    '#ParentingTips #LifeSkills #अभिभावक #ParentConnect'),
  T('ai_attendance_alerts', 'ai_parent', 'Attendance Alerts', 'AI - Attendance Alerts',
    'बच्चे की Attendance — तुरंत पता',
    '🔔 बच्चा absent? Parents को तुरंत पता!',
    'Smart alerts — बच्चा absent हो तो parents को तुरंत सूचना। पूरी पारदर्शिता। FREE platform।',
    '#AttendanceAlerts #ParentConnect #StudentSafety #अभिभावक'),
  T('ai_child_progress', 'ai_parent', 'Child Progress', 'AI - Child Progress',
    'बच्चे की पूरी प्रगति — आपके हाथ में',
    '📊 बच्चे की पूरी प्रगति — आपके हाथ में!',
    'AI बच्चे की detailed progress सरल भाषा में दिखाए — आप हमेशा informed। 100% FREE।',
    '#ChildProgress #ParentConnect #अभिभावक #StudentProgress'),
  T('ai_daily_summary', 'ai_parent', 'Daily Summary', 'AI - Daily Summary',
    'दिन भर का सार — रोज़ शाम',
    '🌇 दिन भर का सार — रोज़ शाम आपके पास!',
    'AI बच्चे के दिन का छोटा summary parents को दे — स्कूल से जुड़ाव। शिक्षा मित्र AI, FREE।',
    '#DailySummary #ParentConnect #अभिभावक #EdTech'),
  T('ai_faq_bot', 'ai_parent', 'FAQ Bot', 'AI - Parent FAQ Bot',
    'Parents के सवालों के तुरंत जवाब',
    '🤖 Parents के सवालों के तुरंत जवाब!',
    'AI आम सवालों के instant जवाब दे — fees, छुट्टी, exam सब। school के calls कम। 100% FREE।',
    '#ParentFAQ #ParentConnect #WhatsAppForSchools', 'SEND_WHATSAPP_MESSAGE'),

  // ---------------- ADMISSION ENGINE ----------------
  T('admission_engine', 'admission', 'Admission Engine', 'Admission - AI Engine',
    'नए Admission लाइए — AI के साथ',
    '🎯 इस साल ज़्यादा Admission — AI के साथ!',
    'Smart seat counter, school finder, 5KM geo-targeting व personalized welcome video — admissions बढ़ाएँ। 100% FREE platform।',
    '#SchoolAdmission #Admission2026 #प्रवेश #AdmissionOpen', 'APPLY_NOW'),

  // ---------------- TRUST & BYODB ----------------
  T('trust_byodb', 'trust', 'Trust + BYODB', 'Trust - BYODB Modern School',
    'Aapka School, Aapka Data, Aapka Domain',
    '🔐 Aapka School, Aapka Data, Aapka Domain!',
    'Shiksha Mitra laaya hai ek modern school management model jisme control aapke paas rahega:\n\n✅ India\'s first Dual Language Support (Hindi First)\n✅ 8 Core Features (Advance ERP)\n✅ 31 AI Features (Unstoppable Growth)\n✅ Admission Automation Engine (Inquiry → Admission, 8 steps, 100% human-less)\n✅ BYODB (100% Data Privacy)\n✅ BYOK (Control your AI usage)\n✅ Custom Domain Connect (Own Branding)\n\nSchool operations ko simple, fast aur intelligent banaiye — ek hi platform par.',
    '#SchoolERP #SchoolTech #AIPoweredSoftware #AdmissionSystem #SchoolAdmission #DigitalSchool #SmartERP #EduTechSolutions #SchoolGrowth #BYOK #BYODB #CustomDomainIntegration #HindiLanguageSupport',
    'SEND_WHATSAPP_MESSAGE'),
];
